import { z } from "zod";
import { marchCargoSchema, resourceNodeInstanceSchema } from "./resourceNodes";

const timestampSchema = z.string().datetime({ offset: true });
const nullableTimestampSchema = timestampSchema.nullable();

export const neighbourhoodInstanceStatusSchema = z.enum([
  "MIGRATION_HOLD",
  "ACTIVE",
  "RETIRED",
]);

export const neighbourhoodMembershipStatusSchema = z.enum([
  "ACTIVE",
  "DORMANT",
  "LEFT",
]);

export const exposureStatusSchema = z.enum(["PROTECTED", "EXPOSED"]);
export const exposureActivationSourceSchema = z.enum(["DIRECT", "RESOURCE"]);

export const marchPurposeSchema = z.enum(["WORLD_HUNT", "RESOURCE", "RAID"]);
export const internalMarchPurposeSchema = z.enum([
  "WORLD_HUNT",
  "RESOURCE",
  "RAID",
  "CONTROLLED_HARNESS",
]);

export const marchStateSchema = z.enum([
  "OUTBOUND",
  "RESOLVING",
  "GATHERING",
  "RETURNING",
  "COMPLETE",
  "CANCELLED",
]);

export const neighbourhoodMembershipSchema = z.object({
  neighbourhoodId: z.string().uuid(),
  playerId: z.string().uuid(),
  castleLocationId: z.string().uuid(),
  stableAssignmentRef: z.string().min(1),
  castleRingIndex: z.number().int().min(1).max(12),
  status: neighbourhoodMembershipStatusSchema,
  dormant: z.boolean(),
  assignmentVersion: z.number().int().positive(),
  joinedAt: timestampSchema,
  leftAt: nullableTimestampSchema,
}).strict();

export const exposureStateSchema = z.object({
  status: exposureStatusSchema,
  activatedAt: nullableTimestampSchema,
  expiresAt: nullableTimestampSchema,
  activationSource: exposureActivationSourceSchema.nullable(),
  stateVersion: z.number().int().nonnegative(),
}).strict().superRefine((value, context) => {
  const hasWindow = value.activatedAt !== null && value.expiresAt !== null;
  if (value.status === "EXPOSED" && !hasWindow) {
    context.addIssue({ code: "custom", message: "An exposed state requires its authoritative time window." });
  }
  if (value.activatedAt && value.expiresAt && Date.parse(value.expiresAt) - Date.parse(value.activatedAt) !== 86_400_000) {
    context.addIssue({ code: "custom", path: ["expiresAt"], message: "Exposure must last exactly 24 hours." });
  }
});

export const universalMarchSchema = z.object({
  contractVersion: z.literal(1),
  id: z.string().uuid(),
  neighbourhoodId: z.string().uuid(),
  ownerPlayerId: z.string().uuid(),
  originLocationId: z.string().uuid(),
  destinationLocationId: z.string().uuid(),
  routeId: z.string().uuid(),
  purpose: internalMarchPurposeSchema,
  state: marchStateSchema,
  activeSlot: z.number().int().min(1).max(2),
  departedAt: timestampSchema,
  arrivesAt: timestampSchema,
  resolvingAt: nullableTimestampSchema,
  returnDepartedAt: nullableTimestampSchema,
  returnArrivesAt: nullableTimestampSchema,
  completedAt: nullableTimestampSchema,
  cancelledAt: nullableTimestampSchema,
  stateVersion: z.number().int().positive(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
}).strict();

export const privateMarchSummarySchema = z.object({
  marchId: z.string().uuid(),
  activityId: z.string().uuid(),
  armySnapshotId: z.string().uuid(),
  contentVersion: z.number().int().positive(),
  formulaVersion: z.string().min(1),
  templateVersion: z.number().int().positive(),
  routeDurationSeconds: z.number().int().positive(),
  releaseOnMarchTerminal: z.literal(true),
}).strict();

export const neighbourhoodStateV1Schema = z.object({
  contractVersion: z.literal(1),
  neighbourhoodId: z.string().uuid(),
  templateId: z.string().uuid(),
  templateVersion: z.number().int().positive(),
  instanceStatus: neighbourhoodInstanceStatusSchema,
  stateVersion: z.number().int().nonnegative(),
  serverNow: timestampSchema,
  currentMember: neighbourhoodMembershipSchema,
  members: z.array(neighbourhoodMembershipSchema),
  exposure: exposureStateSchema,
  marches: z.array(universalMarchSchema),
  privateMarches: z.array(privateMarchSummarySchema),
}).strict();

export const neighbourhoodStateV2Schema = neighbourhoodStateV1Schema
  .omit({ contractVersion: true })
  .extend({
    contractVersion: z.literal(2),
    resourceNodes: z.array(resourceNodeInstanceSchema),
    cargo: z.array(marchCargoSchema),
  })
  .strict();

export const neighbourhoodStateSchema = z.union([
  neighbourhoodStateV1Schema,
  neighbourhoodStateV2Schema,
]);

export function upgradeNeighbourhoodStateV1(
  state: z.infer<typeof neighbourhoodStateV1Schema>,
): z.infer<typeof neighbourhoodStateV2Schema> {
  return { ...state, contractVersion: 2, resourceNodes: [], cargo: [] };
}

export type NeighbourhoodInstanceStatus = z.infer<typeof neighbourhoodInstanceStatusSchema>;
export type NeighbourhoodMembershipStatus = z.infer<typeof neighbourhoodMembershipStatusSchema>;
export type NeighbourhoodMembershipV1 = z.infer<typeof neighbourhoodMembershipSchema>;
export type ExposureStateV1 = z.infer<typeof exposureStateSchema>;
export type ExposureActivationSource = z.infer<typeof exposureActivationSourceSchema>;
export type MarchPurpose = z.infer<typeof marchPurposeSchema>;
export type InternalMarchPurpose = z.infer<typeof internalMarchPurposeSchema>;
export type MarchState = z.infer<typeof marchStateSchema>;
export type UniversalMarchV1 = z.infer<typeof universalMarchSchema>;
export type UniversalMarchPrivateV1 = z.infer<typeof privateMarchSummarySchema>;
export type NeighbourhoodStateV1 = z.infer<typeof neighbourhoodStateV1Schema>;
export type NeighbourhoodStateV2 = z.infer<typeof neighbourhoodStateV2Schema>;
export type NeighbourhoodState = z.infer<typeof neighbourhoodStateSchema>;
